import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  FaTasks,
  FaStopCircle,
  FaChevronDown,
  FaChevronRight,
  FaExclamationCircle,
} from 'react-icons/fa';
import {
  Card,
  SectionTitle,
  StatusBadge,
  ProgressBar,
  Spinner,
  Button,
} from './Primitives';
import { formatDuration, completedFromSummary } from '../utils/format';
import { fetchTaskLog } from '../services/generatorBatchApi';

function TaskLog({ batchId, taskId }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  const load = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    fetchTaskLog(batchId, taskId, undefined, controller.signal)
      .then((data) => setContent(data?.content ?? ''))
      .catch((err) => {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        setError('Impossibile caricare il log.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
  }, [batchId, taskId]);

  useEffect(() => {
    load();
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, [load]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-3 text-xs text-gray-500 dark:text-gray-400">
        <Spinner className="h-3.5 w-3.5" /> Caricamento log…
      </div>
    );
  }
  if (error) {
    return <div className="px-3 py-3 text-xs text-red-600 dark:text-red-400">{error}</div>;
  }
  return (
    <pre className="max-h-64 overflow-auto rounded-lg bg-gray-900 p-3 text-xs leading-relaxed text-gray-100 dark:bg-black/50">
      {content && content.length > 0 ? content : '(log vuoto)'}
    </pre>
  );
}

function TaskRow({ batchId, task }) {
  const [open, setOpen] = useState(false);
  const running = task.status === 'RUNNING';
  const failed = task.status === 'FAILED' || task.status === 'INTERRUPTED';

  return (
    <li className="rounded-xl border border-gray-200 dark:border-gray-700">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <span className="text-gray-400">
          {open ? <FaChevronDown size={11} /> : <FaChevronRight size={11} />}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-gray-500 dark:text-gray-400">{task.id}</span>
            <span className="truncate text-sm font-medium text-gray-800 dark:text-gray-100">
              {task.table?.name}
            </span>
          </span>
          <span
            className="mt-0.5 block truncate text-xs text-gray-500 dark:text-gray-400"
            title={task.skeleton?.path}
          >
            {task.skeleton?.path?.split('/').pop()}
          </span>
        </span>

        {running && <Spinner className="h-3.5 w-3.5" />}
        <span className="text-xs text-gray-400 tabular-nums">
          {formatDuration(task.startedAt, task.finishedAt)}
        </span>
        <StatusBadge status={task.status} />
      </button>

      {failed && task.error && (
        <div className="mx-3 mb-2 flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
          <FaExclamationCircle size={12} className="mt-0.5 shrink-0" />
          <span className="break-words">{String(task.error)}</span>
        </div>
      )}

      {open && (
        <div className="border-t border-gray-100 p-3 dark:border-gray-700">
          <TaskLog batchId={batchId} taskId={task.id} />
        </div>
      )}
    </li>
  );
}

/**
 * Step 5: progressione complessiva e per singolo task, con gestione di tutti gli
 * stati terminali, errori e log. Assume che i task possano fallire: lo stato del
 * batch guida i colori e i messaggi.
 */
export default function RunProgress({ batch, polling, cancelling, onCancel }) {
  if (!batch) return null;

  const summary = batch.summary || {};
  const done = completedFromSummary(summary);
  const total = summary.total || 0;
  const tasks = batch.tasks || [];
  const isActive = batch.status === 'QUEUED' || batch.status === 'RUNNING';

  const barColor =
    batch.status === 'SUCCESS'
      ? 'bg-emerald-500'
      : batch.status === 'FAILED'
        ? 'bg-red-500'
        : batch.status === 'PARTIAL'
          ? 'bg-amber-500'
          : batch.status === 'CANCELLED' || batch.status === 'INTERRUPTED'
            ? 'bg-gray-400'
            : 'bg-primary';

  return (
    <Card className="p-5">
      <SectionTitle
        icon={FaTasks}
        title="Generazione"
        subtitle={`Batch ${batch.batchId?.slice(0, 8)}…`}
        action={
          <div className="flex items-center gap-2">
            <StatusBadge status={batch.status} />
            {isActive && (
              <Button
                variant="danger"
                icon={FaStopCircle}
                onClick={onCancel}
                disabled={cancelling || batch.cancelRequested}
                className="!px-3 !py-1.5 text-xs"
              >
                {cancelling ? 'Annullamento…' : 'Annulla'}
              </Button>
            )}
          </div>
        }
      />

      {/* Progressione complessiva */}
      <div className="mt-4">
        <div className="mb-1.5 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>
            {done} / {total} task completati
            {polling && <span className="ml-2 text-primary">· aggiornamento…</span>}
          </span>
          <span className="tabular-nums">
            {summary.success || 0} ok · {summary.failed || 0} err ·{' '}
            {(summary.cancelled || 0) + (summary.interrupted || 0)} altri
          </span>
        </div>
        <ProgressBar done={done} total={total} colorClass={barColor} />
      </div>

      {/* Contatori per stato */}
      <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-6">
        {[
          ['pending', 'In attesa'],
          ['running', 'In corso'],
          ['success', 'Ok'],
          ['failed', 'Falliti'],
          ['cancelled', 'Annullati'],
          ['interrupted', 'Interrotti'],
        ].map(([key, label]) => (
          <div
            key={key}
            className="rounded-xl border border-gray-100 bg-gray-50 px-2 py-2 text-center dark:border-gray-700 dark:bg-gray-900/50"
          >
            <div className="text-lg font-semibold tabular-nums text-gray-800 dark:text-gray-100">
              {summary[key] || 0}
            </div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400">{label}</div>
          </div>
        ))}
      </div>

      {/* Per-task */}
      {tasks.length > 0 && (
        <ul className="mt-5 space-y-2">
          {tasks.map((t) => (
            <TaskRow key={t.id} batchId={batch.batchId} task={t} />
          ))}
        </ul>
      )}
    </Card>
  );
}
